import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useFrontApi } from '~/composables/useFrontApi'
import { useChatStore } from '~/stores/chatStore'
import { TRACKING_EVENTS } from '~/constants/trackingEvents'
import type { ChatAttachment } from '~/types/ChatAttachment'

type ChatAttachmentsStoreStatus = 'idle' | 'loading' | 'ready' | 'error'

export const useChatAttachmentsStore = defineStore('chatAttachments', () => {
  const attachments: Ref<ChatAttachment[]> = ref([])
  const status: Ref<ChatAttachmentsStoreStatus> = ref('idle')
  const errorMessage: Ref<string | null> = ref(null)
  const isDocumentsDrawerOpen: Ref<boolean> = ref(false)
  const isDownloadingAttachmentId: Ref<string | null> = ref(null)
  const isUpdatingDocumentCategory: Ref<boolean> = ref(false)

  const attachmentCount: ComputedRef<number> = computed((): number => attachments.value.length)

  /**
   * Ouvre le drawer « Tes documents » et charge la liste si nécessaire.
   * @returns {Promise<void>}
   */
  async function openDocumentsDrawer(): Promise<void> {
    isDocumentsDrawerOpen.value = true
    const { track } = useTracking()
    track(TRACKING_EVENTS.LOLA_ATTACHMENTS_OPENED, {
      attachmentCount: attachments.value.length,
    })
    if (status.value !== 'ready') {
      await fetchAttachments()
    }
  }

  /**
   * Ferme le drawer « Tes documents ».
   * @returns {void}
   */
  function closeDocumentsDrawer(): void {
    isDocumentsDrawerOpen.value = false
  }

  /**
   * Charge la liste des pièces jointes depuis l'API conversation.
   * @returns {Promise<void>}
   */
  async function fetchAttachments(): Promise<void> {
    status.value = 'loading'
    errorMessage.value = null

    try {
      const chatStore = useChatStore()
      await chatStore.loadHistory()

      const api = useFrontApi()
      const { data } = await api.get<ChatAttachment[]>('/api/conversation/attachments')
      attachments.value = data
      status.value = 'ready'
    } catch (error) {
      status.value = 'error'
      errorMessage.value = (error as Error).message
      console.error('[chatAttachmentsStore] fetchAttachments:', error)
    }
  }

  /**
   * Télécharge une pièce jointe via son identifiant API.
   * @param {ChatAttachment} attachment - Pièce jointe à télécharger.
   * @returns {Promise<void>}
   */
  async function downloadAttachment(attachment: ChatAttachment): Promise<void> {
    if (isDownloadingAttachmentId.value) return

    isDownloadingAttachmentId.value = attachment.id
    const { track } = useTracking()

    try {
      const blob: Blob = (
        await useFrontApi().get<Blob>(`/api/conversation/attachments/${attachment.id}`, {
          responseType: 'blob',
        })
      ).data

      const blobUrl: string = URL.createObjectURL(blob)
      const downloadLink: HTMLAnchorElement = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = attachment.fileName || 'document'
      downloadLink.click()
      URL.revokeObjectURL(blobUrl)

      track(TRACKING_EVENTS.LOLA_ATTACHMENT_DOWNLOADED, {
        attachmentId: attachment.id,
        contentType: attachment.contentType,
        documentCategory: attachment.documentCategory,
      })
    } catch (error) {
      console.error('[chatAttachmentsStore] downloadAttachment:', error)
      throw error
    } finally {
      isDownloadingAttachmentId.value = null
    }
  }

  /**
   * Met à jour la catégorie documentaire d'une pièce jointe.
   * @param {string} attachmentId - Identifiant de la pièce jointe.
   * @param {string} documentCategory - Nouvelle catégorie documentaire.
   * @returns {Promise<void>}
   */
  async function updateAttachmentDocumentCategory(
    attachmentId: string,
    documentCategory: string,
  ): Promise<void> {
    const attachment: ChatAttachment | undefined = attachments.value.find(
      (item) => item.id === attachmentId,
    )
    if (!attachment || attachment.documentCategory === documentCategory) return

    const previousCategory: string = attachment.documentCategory
    attachment.documentCategory = documentCategory
    isUpdatingDocumentCategory.value = true

    const { track } = useTracking()

    try {
      const api = useFrontApi()
      await api.patch(`/api/conversation/attachments/${attachmentId}`, { documentCategory })

      track(TRACKING_EVENTS.LOLA_ATTACHMENT_CATEGORY_CHANGED, {
        attachmentId,
        previousCategory,
        documentCategory,
      })
    } catch (error) {
      attachment.documentCategory = previousCategory
      console.error('[chatAttachmentsStore] updateAttachmentDocumentCategory:', error)
      throw error
    } finally {
      isUpdatingDocumentCategory.value = false
    }
  }

  return {
    attachments,
    attachmentCount,
    status,
    errorMessage,
    isDocumentsDrawerOpen,
    isDownloadingAttachmentId,
    isUpdatingDocumentCategory,
    openDocumentsDrawer,
    closeDocumentsDrawer,
    fetchAttachments,
    downloadAttachment,
    updateAttachmentDocumentCategory,
  }
})
